import { ConfirmModal } from '../../../commons/components/ConfirmModal';
import React, { useState } from 'react';
import useApi from '../../../api/backendApiContext';
import { useQueryCacheManager } from '../../../hooks/useQueryCacheManager';
import { useMutation } from '@tanstack/react-query';
import { CancelationReason, Customer } from '../../types';
import useAnalytics from '../../../web-analytics/webAnalyticsContext';
import { analyticsTrack } from '../../../web-analytics/webAnalytics';
import { AnalyticsEvent } from '../../../web-analytics/AnalyticsEvent';
import useAnalyticsPage from '../../../web-analytics/hooks/useAnalyticsPage';
import { Page } from '../../../web-analytics/Page';
import { Multiselect, MultiselectOption } from '../../../commons/components/Multiselect';
import _ from 'lodash';

const CancellationReasons: MultiselectOption[] = [
  { text: 'I didn’t use it as expected', code: 'not_used_as_expected' },
  { text: 'I only used it to build credit', code: 'only_credit' },
  { text: 'I need more features', code: 'missing_features' },
  { text: 'Ease of use was less than expected', code: 'hard_to_use' },
  { text: 'I found an alternative', code: 'alternative' },
  { text: 'It’s too expensive', code: 'expensive' },
  { text: 'Other reason', code: 'other' },
];

export const FeedbackStep = (props: {
  selectedReasons: CancelationReason[];
  onSelectedReasonsChanged: (selectedReasons: CancelationReason[]) => void;
  comment: string;
  onCommentChanged: (comment: string) => void;
  onGoBackClicked: () => void;
  onBillingPlanCanceled: (customer: Customer) => void;
}) => {
  useAnalyticsPage(Page.CANCELLATION_WIZARD_FEEDBACK);

  const {
    selectedReasons,
    onSelectedReasonsChanged,
    comment,
    onCommentChanged,
    onGoBackClicked,
    onBillingPlanCanceled,
  } = props;
  const { billingApi } = useApi();
  const queryCacheManager = useQueryCacheManager();
  const analytics = useAnalytics();

  const { mutate: cancelPlan, status: cancelPlanStatus } = useMutation(
    async (variables: { comment: string; selectedReasons: CancelationReason[] }) =>
      billingApi.cancelBillingPlan({
        comment: variables.comment,
        reasons: variables.selectedReasons.map((x) => x.text).sort(),
      }),
    {
      onError: async () => {
        await queryCacheManager.invalidateBillingCustomer();
        await queryCacheManager.invalidateInvoices();
      },
      onSuccess: async (customer, variables) => {
        await queryCacheManager.updateBillingCustomer(customer);
        await queryCacheManager.invalidateInvoices();
        onBillingPlanCanceled(customer);
        analyticsTrack(analytics, AnalyticsEvent.BILLING_PLAN_CANCELED, {
          reason: variables.selectedReasons.map((x) => x.code),
        });
      },
    }
  );
  const [reasons, setReasons] = useState<(CancelationReason & { isSelected: boolean })[]>(() =>
    _.shuffle(
      CancellationReasons.map((x) => ({
        ...x,
        isSelected: !!selectedReasons.find((r) => r.code === x.code),
      }))
    )
  );

  return (
    <ConfirmModal
      title="Tell us why you are cancelling this plan"
      subTitle="You can select multiple options."
      ariaHideApp={false}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={true}
      footer={{
        type: 'show',
        extraButtons: [
          {
            label: 'Go back',
            variant: 'light',
            disabled: cancelPlanStatus === 'loading' || cancelPlanStatus === 'success',
            onClick: onGoBackClicked,
          },
          {
            label: 'Cancel plan',
            disabled: cancelPlanStatus === 'loading' || cancelPlanStatus === 'success',
            variant: 'danger',
            onClick: () => cancelPlan({ comment, selectedReasons }),
          },
        ],
      }}
    >
      <Multiselect
        options={reasons}
        onOptionsChanged={(options) => {
          onSelectedReasonsChanged(options.filter((x) => x.isSelected));
          setReasons(options);
        }}
      />
      <div className="cancellation-feedback__comment-wrapper">
        <textarea
          className="cancellation-feedback__comment"
          data-test="cancellation-feedback__comment"
          placeholder="Share your feedback with us…"
          value={comment}
          maxLength={1024}
          onChange={(event) => {
            onCommentChanged(event.target.value);
          }}
        />
      </div>
    </ConfirmModal>
  );
};
